/**
 * A-07 — medicine catalogue management.
 *
 * Add button reveals the shared MedicineForm (also used for edit). The list is a server-driven
 * virtual-scroll table (components/ui/VirtualTable: only its rows scroll, so the search and
 * filters above stay in view; the next page loads as you scroll), with search, sort
 * (+ direction), filters (status + unit kind), and a per-page batch size. Row actions: edit (same
 * form) + remove (disable), which first asks for "delete" to be typed.
 */

import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import {
  activateMedicine,
  createMedicine,
  disableMedicine,
  listMedicines,
  updateMedicine,
} from "../../api/admin";
import type { Medicine, MedicineInput, MedicineQuery } from "../../api/admin";
import { describeError } from "../../api/errors";
import { useIdempotencyKey } from "../../api/useIdempotencyKey";
import { ConfirmDelete } from "../../components/ui/ConfirmDelete";
import { ExportButton } from "../../components/ui/ExportButton";
import { FilterBar } from "../../components/ui/FilterBar";
import {
  EditIcon,
  IconButton,
  PlayIcon,
  RowActions,
  TrashIcon,
} from "../../components/ui/IconButton";
import { Modal } from "../../components/ui/Modal";
import { Select } from "../../components/ui/Select";
import { TableRow, VirtualTable } from "../../components/ui/VirtualTable";
import { formatINR } from "../../lib/money";
import { unitLabel } from "../../lib/rxLines";
import type { TableColumn } from "../../lib/tableColumns";
import { byPageNumber, EXPORT_PAGE_SIZE, type ExportColumn } from "../../lib/tableExport";
import { useUnitKinds } from "../../lib/useUnitKinds";
import { MedicineForm } from "./MedicineForm";

const PER_PAGE_OPTS = [25, 50, 100];
const SORTS = [
  { key: "name", label: "Name" },
  { key: "unit_kind", label: "Unit kind" },
  { key: "mrp", label: "MRP" },
  { key: "default_gst_pct", label: "GST %" },
  { key: "created_at", label: "Created" },
];

const ROW_H = 56;
// Name, status and actions at every width; MRP from sm, unit and GST from md (narrower screens
// show them under the name).
const COLUMNS: TableColumn[] = [
  { key: "name", header: "Name", width: { base: "minmax(0,1fr)", lg: "minmax(0,2.5fr)" } },
  { key: "unit", header: "Unit", width: "minmax(0,1fr)", from: "md" },
  { key: "mrp", header: "MRP", width: "6rem", from: "sm", align: "end" },
  { key: "gst", header: "GST %", width: "4.5rem", from: "md", align: "end" },
  { key: "status", header: "Status", width: "5.25rem" },
  { key: "actions", header: "Actions", width: "4.5rem", align: "end" },
];

// The export carries every field, whatever the screen shows.
const EXPORT_COLUMNS: ExportColumn<Medicine>[] = [
  { header: "Name", value: (m) => m.name },
  { header: "Brand", value: (m) => m.brand },
  { header: "Unit", value: (m) => m.unit },
  { header: "Unit kind", value: (m) => unitLabel(m.unit_kind) },
  { header: "Schedule", value: (m) => m.schedule_class },
  { header: "HSN code", value: (m) => m.hsn_code },
  { header: "MRP (₹)", value: (m) => m.mrp },
  { header: "GST %", value: (m) => m.default_gst_pct },
  { header: "Status", value: (m) => (m.active ? "Active" : "Disabled") },
];

export function Medicines() {
  const qc = useQueryClient();
  const unitKinds = useUnitKinds();

  // form (add/edit) state
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Medicine | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  // the medicine whose removal is being confirmed
  const [removing, setRemoving] = useState<Medicine | null>(null);

  // list controls
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<MedicineQuery["status"]>("");
  const [unitKind, setUnitKind] = useState("");
  const [sort, setSort] = useState("name");
  const [descending, setDescending] = useState(false);
  const [perPage, setPerPage] = useState(25);

  const query: MedicineQuery = {
    q,
    status,
    unit_kind: unitKind,
    sort,
    descending,
    per_page: perPage,
  };
  const invalidate = () => qc.invalidateQueries({ queryKey: ["medicines"] });

  const createKey = useIdempotencyKey();
  const create = useMutation({
    mutationFn: (body: MedicineInput) => createMedicine(body, createKey.keyFor(body)),
    onSuccess: (med) => {
      createKey.reset();
      toast.success(med.message ?? "Medicine created successfully.");
      closeForm();
      invalidate();
    },
    onError: (e) => setFormError(describeError(e)),
  });
  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<MedicineInput> }) =>
      updateMedicine(id, body),
    onSuccess: (med) => {
      toast.success(med.message ?? "Medicine updated successfully.");
      closeForm();
      invalidate();
    },
    onError: (e) => setFormError(describeError(e)),
  });
  // A refusal is said, and the list re-read (ADM-10): re-activating a medicine whose name and brand
  // an active one now has was a 409 that showed nothing, leaving the row "Disabled" unexplained.
  const listActionFailed = (e: unknown) => {
    toast.error(describeError(e));
    invalidate();
  };
  const remove = useMutation({
    mutationFn: (id: string) => disableMedicine(id),
    onSuccess: () => {
      // The disable endpoint returns no body, so there's no server message to show.
      toast.success("Medicine disabled successfully.");
      invalidate();
    },
    onError: listActionFailed,
    onSettled: () => setRemoving(null),
  });
  const activate = useMutation({
    mutationFn: (id: string) => activateMedicine(id),
    onSuccess: (med) => {
      toast.success(med.message ?? "Medicine activated successfully.");
      invalidate();
    },
    onError: listActionFailed,
  });

  function closeForm() {
    setFormOpen(false);
    setEditing(null);
    setFormError(null);
  }
  function openAdd() {
    setEditing(null);
    setFormError(null);
    setFormOpen(true);
  }
  function openEdit(m: Medicine) {
    setEditing(m);
    setFormError(null);
    setFormOpen(true);
  }

  const narrowing = (status ? 1 : 0) + (unitKind ? 1 : 0);
  const activeFilters = narrowing + (q ? 1 : 0);

  return (
    <div className="flex h-full flex-col gap-3 sm:gap-5">
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Medicines</h2>
          <p className="text-sm text-gray-500">
            Catalogue used across prescriptions and orders.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Every medicine the search and filters match, in the table's order. */}
          <ExportButton
            table="medicines"
            columns={EXPORT_COLUMNS}
            keyOf={(m) => m.id}
            fetchPage={byPageNumber((page) => listMedicines({ ...query, page, per_page: EXPORT_PAGE_SIZE }))}
          />
          <button
            onClick={openAdd}
            className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-fg shadow-xs transition hover:opacity-90"
          >
            + Add medicine
          </button>
        </div>
      </div>

      <Modal
        open={formOpen}
        title={editing ? "Edit medicine" : "Add medicine"}
        onClose={closeForm}
      >
        <MedicineForm
          initial={editing}
          submitting={create.isPending || update.isPending}
          error={formError}
          onCancel={closeForm}
          onSubmit={(body) =>
            editing
              ? update.mutate({ id: editing.id, body })
              : create.mutate(body)
          }
        />
      </Modal>

      <ConfirmDelete
        open={removing !== null}
        title="Remove medicine?"
        busy={remove.isPending}
        onConfirm={() => removing && remove.mutate(removing.id)}
        onClose={() => setRemoving(null)}
      >
        <p>
          <b className="text-gray-800">{removing?.name}</b>
          {removing?.brand ? ` (${removing.brand})` : ""} will be disabled. You can activate it
          again from this list.
        </p>
      </ConfirmDelete>

      <FilterBar
        label="Search and filter medicines"
        narrowing={narrowing}
        search={
          <Control label="Search" className="min-w-[180px] flex-1">
            <div className="relative">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-500"
                aria-hidden="true"
              >
                <circle cx="11" cy="11" r="7" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name…"
                className="w-full rounded-lg border py-1.5 pl-11 pr-2 outline-hidden focus:border-brand focus:ring-1 focus:ring-brand"
              />
            </div>
          </Control>
        }
      >
        <Control label="Status">
          <Select
            value={status ?? ""}
            options={[
              { value: "", label: "All" },
              { value: "active", label: "Active" },
              { value: "disabled", label: "Disabled" },
            ]}
            onChange={setStatus}
          />
        </Control>
        <Control label="Unit kind">
          <Select
            value={unitKind}
            options={["", ...unitKinds].map((u) => ({ value: u, label: u ? unitLabel(u) : "All" }))}
            onChange={setUnitKind}
          />
        </Control>
        <Control label="Sort">
          <div className="flex">
            <Select
              value={sort}
              options={SORTS.map((s) => ({ value: s.key, label: s.label }))}
              onChange={setSort}
              className="flex-1 rounded-r-none"
            />
            <button
              onClick={() => setDescending((d) => !d)}
              className="select-addon"
              title="Toggle direction"
            >
              {descending ? "↓" : "↑"}
            </button>
          </div>
        </Control>
        <Control label="Batch size">
          <Select
            value={perPage}
            options={PER_PAGE_OPTS.map((n) => ({ value: n, label: String(n) }))}
            onChange={setPerPage}
          />
        </Control>
        {activeFilters > 0 && (
          <button
            onClick={() => {
              setQ("");
              setStatus("");
              setUnitKind("");
            }}
            className="ml-auto self-end rounded-lg px-3 py-1.5 text-gray-500 hover:bg-gray-100"
          >
            Clear filters ({activeFilters})
          </button>
        )}
      </FilterBar>

      <MedicineTable
        query={query}
        onEdit={openEdit}
        onRemove={setRemoving}
        onActivate={(id) => activate.mutate(id)}
      />
    </div>
  );
}

// ---------------- Virtual-scroll table ----------------

function MedicineTable({
  query,
  onEdit,
  onRemove,
  onActivate,
}: {
  query: MedicineQuery;
  onEdit: (m: Medicine) => void;
  onRemove: (m: Medicine) => void;
  onActivate: (id: string) => void;
}) {
  const {
    data,
    error,
    isError,
    isLoading,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
  } = useInfiniteQuery({
    queryKey: ["medicines", query],
    queryFn: ({ pageParam }) => listMedicines({ ...query, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.has_more ? last.page + 1 : undefined),
  });

  const rows = useMemo(() => data?.pages.flatMap((p) => p.data) ?? [], [data]);

  return (
    <VirtualTable
      label="Medicines"
      columns={COLUMNS}
      rows={rows}
      rowKey={(m) => m.id}
      rowHeight={ROW_H}
      loading={isLoading}
      error={isError ? describeError(error) : null}
      onRetry={() => void refetch()}
      empty="No medicines match your filters."
      total={data?.pages[0]?.total}
      hasMore={hasNextPage}
      loadingMore={isFetchingNextPage}
      loadMoreFailed={isFetchNextPageError}
      onLoadMore={fetchNextPage}
      resetKey={JSON.stringify(query)}
      renderRow={(m) => (
        <TableRow
          columns={COLUMNS}
          className="h-full text-sm transition-colors hover:bg-gray-50"
          cells={{
            name: <NameCell m={m} />,
            unit: <span className="block truncate text-gray-600">{m.unit}</span>,
            mrp: <span className="tabular-nums text-gray-600">{formatINR(m.mrp)}</span>,
            gst: <span className="tabular-nums text-gray-600">{m.default_gst_pct}%</span>,
            status: <StatusBadge active={m.active} />,
            actions: (
              <MedicineRowActions
                m={m}
                onEdit={onEdit}
                onRemove={onRemove}
                onActivate={onActivate}
              />
            ),
          }}
        />
      )}
    />
  );
}

/** Name and brand; where the MRP, unit and GST columns are left out, those too — the price first,
 *  since a phone cuts the line short. */
function NameCell({ m }: { m: Medicine }) {
  return (
    <div className="min-w-0">
      <div className="truncate font-medium text-gray-800">{m.name}</div>
      <div className="truncate text-xs text-gray-400">
        {m.brand}
        <span className="sm:hidden">
          {m.brand && " · "}
          {formatINR(m.mrp)}
        </span>
        <span className="md:hidden">
          {m.brand ? " · " : <span className="sm:hidden"> · </span>}
          {m.unit} · GST {m.default_gst_pct}%
        </span>
      </div>
    </div>
  );
}

function StatusBadge({ active }: { active: boolean }) {
  return (
    <span
      className={`inline-flex w-fit items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        active ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"
      }`}
    >
      {active ? "Active" : "Disabled"}
    </span>
  );
}

function MedicineRowActions({
  m,
  onEdit,
  onRemove,
  onActivate,
}: {
  m: Medicine;
  onEdit: (m: Medicine) => void;
  onRemove: (m: Medicine) => void;
  onActivate: (id: string) => void;
}) {
  return (
    <RowActions>
      <IconButton label="Edit" tone="brand" onClick={() => onEdit(m)}>
        <EditIcon />
      </IconButton>
      {m.active ? (
        <IconButton label="Remove" tone="danger" onClick={() => onRemove(m)}>
          <TrashIcon />
        </IconButton>
      ) : (
        <IconButton
          label="Activate"
          tone="success"
          onClick={() => onActivate(m.id)}
        >
          <PlayIcon />
        </IconButton>
      )}
    </RowActions>
  );
}

function Control({
  label,
  children,
  className = "",
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label className={`flex flex-col gap-1 text-xs text-gray-500 ${className}`}>
      {label}
      {children}
    </label>
  );
}
